import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  CreditCard,
  Building,
  FileText,
  Calendar,
  DollarSign,
  Banknote
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import paymentService from '../../services/paymentService'
import customerMasterService from '../../services/customerMasterService'
import paymentMethodMasterService from '../../services/paymentMethodMasterService'
import taxInvoiceService from '../../services/taxInvoiceService'
import CustomerSelectionModal from '../../components/CustomerSelectionModal/CustomerSelectionModal'
import { formatCurrency } from '../../utils/currencyFormat'
import './PaymentForm.css'

const PaymentForm = () => {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('paymentDetails')
  const [loading, setLoading] = useState(false)
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [customers, setCustomers] = useState([])
  const [paymentMethods, setPaymentMethods] = useState([])
  const [invoices, setInvoices] = useState([])
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)
  
  const [formData, setFormData] = useState({
    // Payment Details
    paymentDate: new Date().toISOString().split('T')[0],
    customer: '',
    customerName: '',
    customerCode: '',
    project: '',
    projectName: '',
    invoice: '',
    invoiceNumber: '',
    invoiceDate: '',
    invoiceAmountWithoutGST: '',
    invoiceAmountWithGST: '',
    outstandingAmount: '',
    paymentAmount: '',
    currency: 'INR',
    conversionRate: '',
    paymentMethod: '',
    paymentMethodName: '',
    status: 'Pending',
    
    // Transaction Details
    transactionReference: '',
    chequeNumber: '',
    chequeDate: '',
    bankName: '',
    bankAccountNumber: '',
    ifscCode: '',
    utrNumber: '',
    
    // Additional Information
    notes: '',
    remarks: ''
  })

  // Load payment methods and customers on mount
  useEffect(() => {
    loadPaymentMethods()
    loadCustomers()
  }, [])

  // Load invoices when customer is selected
  useEffect(() => {
    if (formData.customer) {
      loadCustomerInvoices(formData.customer)
    } else {
      setInvoices([])
      setFormData(prev => ({
        ...prev,
        invoice: '',
        invoiceNumber: '',
        invoiceDate: '',
        invoiceAmountWithoutGST: '',
        invoiceAmountWithGST: '',
        outstandingAmount: '',
        project: '',
        projectName: ''
      }))
    }
  }, [formData.customer])

  // Update payment method name when payment method changes
  useEffect(() => {
    if (formData.paymentMethod && paymentMethods.length > 0) {
      const method = paymentMethods.find(m => (m._id || m.id) === formData.paymentMethod)
      if (method) {
        setSelectedPaymentMethod(method)
        setFormData(prev => ({
          ...prev,
          paymentMethodName: method.paymentMethodName || ''
        }))
      }
    }
  }, [formData.paymentMethod, paymentMethods])

  const loadPaymentMethods = async () => {
    try {
      const response = await paymentMethodMasterService.getAllPaymentMethods({ limit: 1000 })
      if (response && response.success) {
        setPaymentMethods(response.data || [])
      }
    } catch (error) {
      console.error('Error loading payment methods:', error)
    }
  }

  const loadCustomers = async () => {
    try {
      const response = await customerMasterService.getAllCustomers({ limit: 1000, status: 'A' })
      if (response && response.success) {
        setCustomers(response.data || [])
      }
    } catch (error) {
      console.error('Error loading customers:', error)
    }
  }

  const loadCustomerInvoices = async (customerId) => {
    try {
      setLoading(true)
      // Load unpaid and partially paid invoices for the customer
      const response = await taxInvoiceService.getAllTaxInvoices({
        customer: customerId,
        paymentStatus: 'Unpaid,Partially Paid',
        limit: 1000
      })
      
      if (response && response.success) {
        const invoices = (response.data || []).map(invoice => {
          // Calculate outstanding amount if not present
          const totalAmount = invoice.totalAmountWithTax || 0
          const paidAmount = invoice.paidAmount || 0
          const outstandingAmount = invoice.outstandingAmount || (totalAmount - paidAmount)
          
          return {
            ...invoice,
            outstandingAmount: Math.max(0, outstandingAmount)
          }
        })
        
        // Sort by invoice date (newest first) and then by invoice number
        invoices.sort((a, b) => {
          const dateA = new Date(a.serviceInvoiceDate || 0)
          const dateB = new Date(b.serviceInvoiceDate || 0)
          if (dateB.getTime() !== dateA.getTime()) {
            return dateB.getTime() - dateA.getTime()
          }
          return (b.serviceInvoiceNumber || '').localeCompare(a.serviceInvoiceNumber || '')
        })
        
        setInvoices(invoices)
      } else {
        setInvoices([])
      }
    } catch (error) {
      console.error('Error loading invoices:', error)
      toast.error('Failed to load customer invoices')
      setInvoices([])
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleCustomerSearch = () => {
    setShowCustomerModal(true)
  }

  const handleCustomerSelect = (selectedCustomer) => {
    setFormData(prev => ({
      ...prev,
      customer: selectedCustomer._id || selectedCustomer.id,
      customerName: selectedCustomer.customerName,
      customerCode: selectedCustomer.customerCode,
      currency: selectedCustomer.customerCurrency || 'INR'
    }))
    setShowCustomerModal(false)
  }

  const handleInvoiceChange = (e) => {
    const invoiceId = e.target.value
    const selectedInvoice = invoices.find(inv => (inv._id || inv.id) === invoiceId)
    
    if (selectedInvoice) {
      const invoiceDate = selectedInvoice.serviceInvoiceDate 
        ? new Date(selectedInvoice.serviceInvoiceDate).toISOString().split('T')[0]
        : ''
      
      const totalAmount = selectedInvoice.totalAmountWithTax || 0
      const paidAmount = selectedInvoice.paidAmount || 0
      const outstandingAmount = selectedInvoice.outstandingAmount || (totalAmount - paidAmount)
      const totalValue = selectedInvoice.totalValue || 0
      
      setFormData(prev => ({
        ...prev,
        invoice: invoiceId,
        invoiceNumber: selectedInvoice.serviceInvoiceNumber || '',
        invoiceDate: invoiceDate,
        invoiceAmountWithoutGST: totalValue.toFixed(2),
        invoiceAmountWithGST: totalAmount.toFixed(2),
        outstandingAmount: outstandingAmount.toFixed(2),
        currency: selectedInvoice.currency || prev.currency || 'INR',
        conversionRate: (selectedInvoice.currency || 'INR') === 'INR' ? '' : prev.conversionRate,
        project: selectedInvoice.project?._id || selectedInvoice.project || '',
        projectName: selectedInvoice.projectName || (selectedInvoice.project?.projectName || '')
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        invoice: '',
        invoiceNumber: '',
        invoiceDate: '',
        invoiceAmountWithoutGST: '',
        invoiceAmountWithGST: '',
        outstandingAmount: '',
        project: '',
        projectName: ''
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validation
    if (!formData.customer) {
      toast.error('Please select a customer')
      return
    }
    if (!formData.paymentAmount || parseFloat(formData.paymentAmount) <= 0) {
      toast.error('Please enter a valid payment amount')
      return
    }
    if (!formData.paymentMethod) {
      toast.error('Please select a payment method')
      return
    }
    if (!formData.paymentDate) {
      toast.error('Please select a payment date')
      return
    }
    if (formData.currency !== 'INR' && !(parseFloat(formData.conversionRate) > 0)) {
      toast.error(`Enter how many rupees were received for 1 ${formData.currency}`)
      return
    }

    setLoading(true)
    try {
      const paymentData = {
        paymentDate: formData.paymentDate,
        customer: formData.customer,
        invoice: formData.invoice || undefined,
        paymentAmount: parseFloat(formData.paymentAmount),
        currency: formData.currency,
        conversionRate: formData.currency === 'INR' ? 1 : parseFloat(formData.conversionRate),
        paymentMethod: formData.paymentMethod,
        status: formData.status,
        transactionReference: formData.transactionReference || undefined,
        chequeNumber: formData.chequeNumber || undefined,
        chequeDate: formData.chequeDate || undefined,
        bankName: formData.bankName || undefined,
        bankAccountNumber: formData.bankAccountNumber || undefined,
        ifscCode: formData.ifscCode || undefined,
        utrNumber: formData.utrNumber || undefined,
        notes: formData.notes || undefined,
        remarks: formData.remarks || undefined
      }

      const response = await paymentService.createPayment(paymentData)
      
      if (response && response.success) {
        toast.success('Payment created successfully!')
        navigate('/accounts/payments')
      } else {
        toast.error(response?.message || 'Failed to create payment')
      }
    } catch (error) {
      console.error('Error creating payment:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
        navigate('/login')
      } else {
        toast.error('Failed to create payment')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setFormData({
      paymentDate: new Date().toISOString().split('T')[0],
      customer: '',
      customerName: '',
      customerCode: '',
      invoice: '',
      invoiceNumber: '',
      invoiceDate: '',
      invoiceAmountWithoutGST: '',
      invoiceAmountWithGST: '',
      outstandingAmount: '',
      project: '',
      projectName: '',
      paymentAmount: '',
      currency: 'INR',
      conversionRate: '',
      paymentMethod: '',
      paymentMethodName: '',
      status: 'Pending',
      transactionReference: '',
      chequeNumber: '',
      chequeDate: '',
      bankName: '',
      bankAccountNumber: '',
      ifscCode: '',
      utrNumber: '',
      notes: '',
      remarks: ''
    })
    setInvoices([])
    setSelectedPaymentMethod(null)
  }

  const handleBack = () => {
    navigate('/accounts/payments')
  }

  const moneyLabel = (amount, currency = formData.currency) => {
    if (amount === '' || amount == null || Number.isNaN(parseFloat(amount))) return ''
    return formatCurrency(parseFloat(amount), currency || 'INR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  const foreignCurrency = formData.currency && formData.currency !== 'INR'
  const rupeesReceived = foreignCurrency && parseFloat(formData.conversionRate) > 0 && parseFloat(formData.paymentAmount) > 0
    ? parseFloat(formData.paymentAmount) * parseFloat(formData.conversionRate)
    : null

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Accounts',
      path: '/accounts',
      onClick: () => navigate('/accounts')
    },
    {
      label: 'Transactions',
      path: '/accounts?tab=transactions',
      onClick: () => navigate('/accounts?tab=transactions')
    },
    {
      label: 'Payments',
      path: '/accounts/payments',
      onClick: () => navigate('/accounts/payments')
    },
    {
      label: 'Create Payment',
      path: '/accounts/payments/new',
      isCurrent: true
    }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Payment Entry</h1>
          <p>Record customer payment received</p>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container pay-entry">
        <div className="sac-form-header">
          <h2>Payment Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'paymentDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('paymentDetails')}
          >
            <CreditCard size={18} />
            <span>Payment Details</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'transactionDetails' ? 'active' : ''}`}
            onClick={() => setActiveTab('transactionDetails')}
          >
            <Banknote size={18} />
            <span>Transaction Details</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'additionalInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('additionalInfo')}
          >
            <FileText size={18} />
            <span>Additional Information</span>
          </button>
        </div>
          
        <form className="form-content" onSubmit={handleSubmit}>
          {/* Payment Details Tab */}
          {activeTab === 'paymentDetails' && (
            <div className="tab-content">
              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Receipt</h3>
                    <p>Who paid, when the money arrived, and how it was received.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-4">
                <div className="form-group">
                  <label htmlFor="paymentDate" className="form-label required">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    id="paymentDate"
                    name="paymentDate"
                    value={formData.paymentDate}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customer" className="form-label required">
                    Customer
                  </label>
                  <input
                    type="text"
                    id="customer"
                    name="customerName"
                    value={formData.customerName || ''}
                    onClick={handleCustomerSearch}
                    readOnly
                    className="form-input pay-pick"
                    placeholder="Click to search customer"
                    required
                  />
                  {formData.customerCode && (
                    <span className="pay-code">{formData.customerCode}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="paymentAmount" className="form-label required">
                    Amount received
                  </label>
                  <input
                    type="number"
                    id="paymentAmount"
                    name="paymentAmount"
                    value={formData.paymentAmount}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    required
                  />
                  <span className="pay-hint">In {formData.currency || 'INR'}.</span>
                </div>

                <div className="form-group">
                  <label htmlFor="currency" className="form-label required">
                    Currency
                  </label>
                  <select
                    id="currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    className="form-input"
                    required={!formData.invoice}
                    disabled={Boolean(formData.invoice)}
                  >
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                    <option value="AUD">AUD</option>
                  </select>
                </div>
              </div>
              <div className="pay-grid pay-grid-2 pay-follow">
                <div className="form-group">
                  <label htmlFor="paymentMethod" className="form-label required">
                    Payment Method
                  </label>
                  <select
                    id="paymentMethod"
                    name="paymentMethod"
                    value={formData.paymentMethod}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="">Select Payment Method</option>
                    {paymentMethods.map(method => (
                      <option key={method._id || method.id} value={method._id || method.id}>
                        {method.paymentMethodName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="status" className="form-label required">
                    Status
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="form-input"
                    required
                  >
                    <option value="Pending">Pending</option>
                    <option value="Cleared">Cleared</option>
                    <option value="Bounced">Bounced</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
              </section>

              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Invoice</h3>
                    <p>Apply this receipt to an open invoice, or leave it blank for an advance.</p>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="invoice" className="form-label">
                    Invoice
                  </label>
                  <select
                    id="invoice"
                    name="invoice"
                    value={formData.invoice}
                    onChange={handleInvoiceChange}
                    className="form-input"
                    disabled={!formData.customer || loading}
                  >
                    <option value="">Select Invoice</option>
                    {loading ? (
                      <option value="" disabled>Loading invoices...</option>
                    ) : invoices.length === 0 && formData.customer ? (
                      <option value="" disabled>No unpaid invoices found</option>
                    ) : (
                      invoices.map(invoice => {
                        const invoiceAmount = invoice.totalAmountWithTax || 0
                        const paidAmount = invoice.paidAmount || 0
                        const outstandingAmount = invoice.outstandingAmount || (invoiceAmount - paidAmount)
                        const invoiceDate = invoice.serviceInvoiceDate
                          ? new Date(invoice.serviceInvoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
                          : ''
                        const code = invoice.currency || 'INR'
                        return (
                          <option key={invoice._id || invoice.id} value={invoice._id || invoice.id}>
                            {invoice.serviceInvoiceNumber} {invoiceDate ? `(${invoiceDate})` : ''} - {moneyLabel(invoiceAmount, code)}
                            {invoice.paymentStatus === 'Partially Paid' && ` | Paid: ${moneyLabel(paidAmount, code)}, Outstanding: ${moneyLabel(outstandingAmount, code)}`}
                            {invoice.paymentStatus === 'Unpaid' && ` | Outstanding: ${moneyLabel(outstandingAmount, code)}`}
                          </option>
                        )
                      })
                    )}
                  </select>
                </div>
                <div className="pay-facts">
                  <div className="pay-fact">
                    <span>Invoice date</span>
                    <strong>{formData.invoiceDate ? new Date(formData.invoiceDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</strong>
                  </div>
                  <div className="pay-fact">
                    <span>Project</span>
                    <strong>{formData.projectName || '—'}</strong>
                  </div>
                  <div className="pay-fact">
                    <span>Still to collect</span>
                    <strong>{formData.outstandingAmount ? moneyLabel(formData.outstandingAmount) : '—'}</strong>
                  </div>
                  <div className="pay-fact">
                    <span>Before tax</span>
                    <strong>{formData.invoiceAmountWithoutGST ? moneyLabel(formData.invoiceAmountWithoutGST) : '—'}</strong>
                  </div>
                  <div className="pay-fact">
                    <span>Invoice total</span>
                    <strong>{formData.invoiceAmountWithGST ? moneyLabel(formData.invoiceAmountWithGST) : '—'}</strong>
                  </div>
                </div>
              </section>

              {foreignCurrency && (
                <section className="pay-block pay-convert">
                  <div className="pay-block-head">
                    <div>
                      <h3>Convert to INR</h3>
                      <p>The invoice stays in {formData.currency}. This rate is only for the company total.</p>
                    </div>
                  </div>
                  <div className="pay-convert-row">
                    <div className="form-group">
                      <label htmlFor="conversionRate" className="form-label required">
                        Rupees per 1 {formData.currency}
                      </label>
                      <input
                        type="number"
                        id="conversionRate"
                        name="conversionRate"
                        value={formData.conversionRate}
                        onChange={handleInputChange}
                        className="form-input"
                        placeholder="0.00"
                        step="0.0001"
                        min="0"
                        required
                      />
                      <span className="pay-hint">The rate your bank used when this {formData.currency} was received.</span>
                    </div>
                    <div className="pay-inr">
                      <span>Amount in INR</span>
                      <strong>{rupeesReceived == null ? '—' : moneyLabel(rupeesReceived, 'INR')}</strong>
                      <p>{rupeesReceived == null ? 'Enter the rate to calculate the rupee amount.' : 'This is the figure added to the company total.'}</p>
                    </div>
                  </div>
                </section>
              )}
            </div>
          )}

          {/* Transaction Details Tab */}
          {activeTab === 'transactionDetails' && (
            <div className="tab-content">
              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Reference</h3>
                    <p>The bank reference, cheque, or UTR for this receipt.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-4">
                <div className="form-group">
                  <label htmlFor="transactionReference" className="form-label">
                    Transaction Reference
                  </label>
                  <input
                    type="text"
                    id="transactionReference"
                    name="transactionReference"
                    value={formData.transactionReference}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter transaction reference"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="chequeNumber" className="form-label">
                    Cheque Number
                  </label>
                  <input
                    type="text"
                    id="chequeNumber"
                    name="chequeNumber"
                    value={formData.chequeNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter cheque number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="chequeDate" className="form-label">
                    Cheque Date
                  </label>
                  <input
                    type="date"
                    id="chequeDate"
                    name="chequeDate"
                    value={formData.chequeDate}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="utrNumber" className="form-label">
                    UTR Number
                  </label>
                  <input
                    type="text"
                    id="utrNumber"
                    name="utrNumber"
                    value={formData.utrNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter UTR number"
                  />
                </div>
              </div>
              </section>

              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Bank</h3>
                    <p>The account the money was received into.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-3">
                <div className="form-group">
                  <label htmlFor="bankName" className="form-label">
                    Bank Name
                  </label>
                  <input
                    type="text"
                    id="bankName"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter bank name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="bankAccountNumber" className="form-label">
                    Bank Account Number
                  </label>
                  <input
                    type="text"
                    id="bankAccountNumber"
                    name="bankAccountNumber"
                    value={formData.bankAccountNumber}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter account number"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="ifscCode" className="form-label">
                    IFSC Code
                  </label>
                  <input
                    type="text"
                    id="ifscCode"
                    name="ifscCode"
                    value={formData.ifscCode}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter IFSC code"
                  />
                </div>
              </div>
              </section>
            </div>
          )}

          {/* Additional Information Tab */}
          {activeTab === 'additionalInfo' && (
            <div className="tab-content">
              <section className="pay-block">
                <div className="pay-block-head">
                  <div>
                    <h3>Notes</h3>
                    <p>Anything else that should stay with this receipt.</p>
                  </div>
                </div>
              <div className="pay-grid pay-grid-2">
                <div className="form-group">
                  <label htmlFor="notes" className="form-label">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="form-input"
                    rows="4"
                    placeholder="Enter any additional notes"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="remarks" className="form-label">
                    Remarks
                  </label>
                  <textarea
                    id="remarks"
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                    className="form-input"
                    rows="4"
                    placeholder="Enter any remarks"
                  />
                </div>
              </div>
              </section>
            </div>
          )}

          {/* Form Actions */}
          <div className="form-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleBack}
            >
              <ArrowLeft size={16} />
              Back
            </button>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleReset}
              >
                <RotateCcw size={16} />
                Reset
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save Payment'}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Customer Selection Modal */}
      {showCustomerModal && (
        <CustomerSelectionModal
          isOpen={showCustomerModal}
          onClose={() => setShowCustomerModal(false)}
          onSelectCustomer={handleCustomerSelect}
        />
      )}
    </div>
  )
}

export default PaymentForm

